import { Injectable } from '@angular/core';
import { StateToken, State, Action, StateContext, Selector, NgxsOnInit } from '@ngxs/store';
import { tap } from 'rxjs/operators';
import { patch } from '@ngxs/store/operators';
import { ApiResponse, ApiResponseWithLocations } from 'src/app/core/models/api-response';
import { DueListApiService } from '../services/due-list-api.service';
import { IDueListItem } from '../models/due-list-item.model';
import {
  LoadDueListCounts,
  LoadDueListDueCompetences,
  LoadDueListItems,
  LoadDueListWarnings,
  LoadMyDueListItems,
  LoadMyDueListWarnings,
  PrintDueListWarnings,
  SaveLastDueListItem,
  ClearLastDueListItem,
  LoadLastDueListItem,
} from './due-list.actions';
import { IDueListCounts } from '../models/due-list-counts.model';

export interface DueListStateModel {
  warnings: IDueListItem[];
  dueCompetences: IDueListItem[];
  locations: string[];
  summaryOfWarnings: number;
  currentPrintedDueListWarnings: Blob | undefined;
  warningsTotalPages: number;
  myWarningsTotalPages: number;
  competencesTotalPages: number;
  dueListCounts: IDueListCounts | undefined;
  lastItem?: IDueListItem;
}

const DUE_LIST_STATE_TOKEN = new StateToken<DueListStateModel>('dueList');
const DUE_LIST_LAST_ITEM_KEY = 'dueListLastItem';

@State<DueListStateModel>({
  name: DUE_LIST_STATE_TOKEN,
  defaults: {
    warnings: [],
    dueCompetences: [],
    locations: [],
    summaryOfWarnings: 0,
    currentPrintedDueListWarnings: undefined,
    warningsTotalPages: 0,
    myWarningsTotalPages: 0,
    competencesTotalPages: 0,
    dueListCounts: undefined,
    lastItem: undefined,
  },
})
@Injectable()
export class DueListState implements NgxsOnInit {
  constructor(private service: DueListApiService) {}

  ngxsOnInit(ctx: StateContext<DueListStateModel>) {
    ctx.dispatch(new LoadLastDueListItem());
  }

  @Action(LoadDueListItems, { cancelUncompleted: true })
  loadDueListItems(ctx: StateContext<DueListStateModel>) {
    ctx.dispatch([
      new LoadDueListWarnings(),
      new LoadDueListDueCompetences(),
      new LoadDueListCounts(),
    ]);
  }

  @Action(LoadMyDueListItems, { cancelUncompleted: true })
  loadMyDueListItems(ctx: StateContext<DueListStateModel>) {
    // Do not dispatch LoadMyDueListWarnings here: app-main-table on the due list warnings tab
    // loads myWarnings with paging/filters. A prior request here is cancelled by that load
    // (cancelUncompleted) and shows as aborted in DevTools.
    ctx.dispatch([new LoadDueListDueCompetences(), new LoadDueListCounts()]);
  }

  @Action(LoadDueListWarnings, { cancelUncompleted: true })
  loadDueListWarnings(
    ctx: StateContext<DueListStateModel>,
    { filter, displayRuntimeChildren }: LoadDueListWarnings
  ) {
    return this.service.getDueListWarnings(filter, displayRuntimeChildren).pipe(
      tap((response: ApiResponseWithLocations<IDueListItem[]>) => {
        if (response) {
          ctx.setState(
            patch({
              warnings: response.data,
              warningsTotalPages: response.totalPages,
              locations: response.locations,
            })
          );
        }
      })
    );
  }

  @Action(LoadDueListDueCompetences, { cancelUncompleted: true })
  loadDueListDueCompetences(
    ctx: StateContext<DueListStateModel>,
    { filter, displayRuntimeChildren }: LoadDueListDueCompetences
  ) {
    return this.service.getDueListDueCompetences(filter, displayRuntimeChildren).pipe(
      tap((response: ApiResponse<IDueListItem[]>) => {
        if (response) {
          ctx.setState(
            patch({ dueCompetences: response.data, competencesTotalPages: response.totalPages })
          );
        }
      })
    );
  }

  @Action(LoadMyDueListWarnings, { cancelUncompleted: true })
  loadMyDueListWarnings(
    ctx: StateContext<DueListStateModel>,
    { filter, displayRuntimeChildren }: LoadMyDueListWarnings
  ) {
    return this.service.getMyDueListWarnings(filter, displayRuntimeChildren).pipe(
      tap((response: ApiResponseWithLocations<IDueListItem[]>) => {
        if (response) {
          ctx.setState(
            patch({
              warnings: response.data,
              myWarningsTotalPages: response.totalPages,
              locations: response.locations,
            })
          );
        }
      })
    );
  }

  @Action(PrintDueListWarnings, { cancelUncompleted: true })
  printDueListWarnings(
    { setState }: StateContext<DueListStateModel>,
    { request }: PrintDueListWarnings
  ) {
    return this.service.print(request).pipe(
      tap((file) => {
        setState(patch({ currentPrintedDueListWarnings: file }));
      })
    );
  }

  @Action(LoadDueListCounts, { cancelUncompleted: true })
  loadDueListCounts(ctx: StateContext<DueListStateModel>) {
    return this.service.getDueListCounts().pipe(
      tap((response: IDueListCounts) => {
        if (response) {
          ctx.setState(patch({ dueListCounts: response }));
        }
      })
    );
  }

  @Action(SaveLastDueListItem)
  saveLastDueListItem(ctx: StateContext<DueListStateModel>, { item }: SaveLastDueListItem) {
    ctx.patchState({ lastItem: { ...item } });
    localStorage.setItem(DUE_LIST_LAST_ITEM_KEY, JSON.stringify(item));
  }

  @Action(ClearLastDueListItem)
  clearLastDueListItem(ctx: StateContext<DueListStateModel>) {
    ctx.patchState({ lastItem: undefined });
    localStorage.removeItem(DUE_LIST_LAST_ITEM_KEY);
  }

  @Action(LoadLastDueListItem)
  loadLastDueListItem(ctx: StateContext<DueListStateModel>) {
    const lastItemString = localStorage.getItem(DUE_LIST_LAST_ITEM_KEY);
    if (lastItemString) {
      try {
        const lastItem = JSON.parse(lastItemString);
        ctx.patchState({ lastItem });
      } catch {
        ctx.patchState({ lastItem: undefined });
      }
    } else {
      ctx.patchState({ lastItem: undefined });
    }
  }

  @Selector()
  static getDueListSummaryNumber(state: DueListStateModel): number {
    const warningsAmount = state.warnings?.length;
    const dueCompetencesAmount = state.dueCompetences?.length;
    const summary = warningsAmount + dueCompetencesAmount;
    if (summary > 0) {
      return summary;
    }
    return state.summaryOfWarnings;
  }

  @Selector()
  static getWarnings(state: DueListStateModel): IDueListItem[] {
    return state.warnings;
  }

  @Selector()
  static getTotalPagesForWarnings(state: DueListStateModel): number {
    return state.warningsTotalPages;
  }

  @Selector()
  static getLocations(state: DueListStateModel): string[] {
    return state.locations;
  }

  @Selector()
  static getTotalPagesForCompetences(state: DueListStateModel): number {
    return state.competencesTotalPages;
  }

  @Selector()
  static getTotalPagesForMyWarnings(state: DueListStateModel): number {
    return state.myWarningsTotalPages;
  }

  @Selector()
  static getDueCompetences(state: DueListStateModel): IDueListItem[] {
    return state.dueCompetences;
  }

  @Selector()
  static getCurrentPrintedDueListWarnings(state: DueListStateModel) {
    return state.currentPrintedDueListWarnings;
  }

  @Selector()
  static getDueListCounts(state: DueListStateModel): IDueListCounts | undefined {
    return state.dueListCounts;
  }

  @Selector()
  static getLastDueListItem(state: DueListStateModel): IDueListItem | undefined {
    return state.lastItem;
  }
}
